import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { DynamicFormControl } from 'src/app/shared/forms/models/dynamic-form-control.model';
import { ControlType } from 'src/app/shared/forms/models/control-type.enum';
import { ActiveFilter } from '../models/active-filter.model';

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  private _initializationKey: string = '';
  private _filterFormControlsMap: Map<string, DynamicFormControl[]>;
  private _activeFiltersMap: Map<string, ActiveFilter[]>;
  public filterFormControls: DynamicFormControl[];
  public activeFilters: ActiveFilter[];

  activeFilterChanges$: Subject<ActiveFilter[]>;
  filterFormControlChanges$: Subject<DynamicFormControl[]>;
  activeFilterRemoved$: Subject<ActiveFilter>;

  constructor() {
    this._filterFormControlsMap = new Map<string, DynamicFormControl[]>();
    this._activeFiltersMap = new Map<string, ActiveFilter[]>();
    this.activeFilterChanges$ = new Subject<ActiveFilter[]>();
    this.filterFormControlChanges$ = new Subject<DynamicFormControl[]>();
    this.activeFilterRemoved$ = new Subject<ActiveFilter>();

    this.activeFilterChanges$.subscribe((i) => {
      this.activeFilters = i;
    });
    this.filterFormControlChanges$.subscribe((i) => {
      this.filterFormControls = i;
    });
  }

  initialize(key: string) {
    this._initializationKey = key;
    this._filterFormControlsMap = this.getFilterFormControlsByKey(key);
    this._activeFiltersMap = this.getActiveFiltersByKey(key);

    this.activeFilters = this._activeFiltersMap.get(key);
    this.filterFormControls = this._filterFormControlsMap.get(key);

    this.activeFilterChanges$.next(this._activeFiltersMap.get(key));
    this.filterFormControlChanges$.next(this._filterFormControlsMap.get(key));
  }

  convertToQueryFilter(
    filterItems: ActiveFilter[],
    usePropertyValue: boolean = false,
  ) {
    var object: any = {};
    filterItems.forEach((i) => {
      // NOTE: Using a boolean here for now to determine which value to send in the request
      // Submissions & User Facilities are filtered in different ways (facility number vs id)
      if (i.controlType === ControlType.MULTISELECT) {
        const value = usePropertyValue ? i.propertyValue : i.value;
        if (!object[i.propertyBinding]) {
          object[i.propertyBinding] = [value.trim()];
        } else {
          object[i.propertyBinding] = [
            ...object[i.propertyBinding],
            value.trim(),
          ];
        }
      }
      if (i.controlType === ControlType.SELECT) {
        const value = usePropertyValue ? i.propertyValue : i.value;
        object[i.propertyBinding] = value.trim();
      }

      if (i.controlType === ControlType.INPUT) {
        object[i.propertyBinding] = i.propertyValue;
      }

      if (i.controlType == ControlType.DATERANGE) {
        object[i.propertyBinding] = i.propertyValue;
      }
    });

    return object;
  }

  setFilterFormControls(filters: DynamicFormControl[]) {
    this._filterFormControlsMap.set(this._initializationKey, filters);
    this.filterFormControlChanges$.next(filters);
  }

  setActiveFilters(filters: ActiveFilter[]) {
    this._activeFiltersMap.set(this._initializationKey, filters);
    this.activeFilterChanges$.next(filters);
  }

  private getFilterFormControlsByKey(
    key: string,
  ): Map<string, DynamicFormControl[]> {
    if (
      this._filterFormControlsMap.has(key) &&
      this._filterFormControlsMap.get(key).length > 0
    ) {
      return this._filterFormControlsMap;
    }
    return this._filterFormControlsMap.set(key, []);
  }

  private getActiveFiltersByKey(key: string): Map<string, ActiveFilter[]> {
    if (
      this._activeFiltersMap.has(key) &&
      this._activeFiltersMap.get(key).length > 0
    ) {
      return this._activeFiltersMap;
    }
    return this._activeFiltersMap.set(key, []);
  }

  removeActiveFilter(item: ActiveFilter) {
    this._activeFiltersMap
      .get(this._initializationKey)
      .splice(
        this._activeFiltersMap.get(this._initializationKey).indexOf(item),
        1,
      );
    this.activeFilterChanges$.next(
      this._activeFiltersMap.get(this._initializationKey),
    );
    this.activeFilterRemoved$.next(item);
  }

  removeAllActiveFilters() {
    this._activeFiltersMap.get(this._initializationKey).forEach((i) => {
      this.activeFilterRemoved$.next(i);
    });

    this._activeFiltersMap.set(this._initializationKey, []);
    this.activeFilterChanges$.next(
      this._activeFiltersMap.get(this._initializationKey),
    );
  }
}
